import { lazy, type ComponentType } from "react";

/**
 * Stale-chunk handling.
 *
 * Every route is a hashed lazy chunk. When a new build is published while a tab
 * is open, the old hashed filenames 404 and the dynamic import throws
 * "Failed to fetch dynamically imported module". A single reload fixes it
 * because the new HTML references the new hashes.
 */

const RELOAD_FLAG = "chunk_reload_attempted";

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "loading chunk",
  "loading css chunk",
  "dynamically imported module",
];

export function isChunkLoadError(error: unknown): boolean {
  const msg =
    typeof error === "string"
      ? error
      : ((error as { message?: string } | null)?.message ?? "");
  const lower = msg.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => lower.includes(p));
}

/** Reload at most once per tab session so we can never loop. */
export function reloadOnceForNewVersion(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still attempt a single reload
  }
  window.location.reload();
  return true;
}

export function clearChunkReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

/**
 * Drop-in replacement for React.lazy that retries once, then falls back to a
 * guarded full reload when the chunk is genuinely gone (new deploy).
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;

      // Transient network blip? Give it one more shot.
      await new Promise((r) => setTimeout(r, 600));
      try {
        return await factory();
      } catch (err2) {
        if (isChunkLoadError(err2) && reloadOnceForNewVersion()) {
          // Keep Suspense pending while the page reloads.
          return await new Promise<{ default: T }>(() => {});
        }
        throw err2;
      }
    }
  });
}
